/**
 * Created by danielg on 9/29/14.
 *
 * Up to date documentation can be found here:
 * https://docs.google.com/a/liveperson.com/document/d/1-a0yz60mMfkhugn2owmFOlvIAl_wtS06v7NY_0soLvc/edit?usp=sharing
 *
 * This is the configuration JSON for a categories-based widget.
 * The idea is that the widget will be created with a location in the model that it watches for updates.
 * This model location is the "widget" context. We will also define a "section" context for each category and "row"
 * context for each row.
 * Every value will have the following style:
 * {
 *    rowPath: "name"
 * }
 * Or:
 * {
 *    sectionPath: "orderId"
 * }
 * Or:
 * {
 *    widgetPath: "skill"
 * }
 *
 * The context is defined by the type of path, and it defines where to look for the value. The path defines the value's
 * location within that context. The path can be separated by '.'
 *
 * So a rowPath defines a value within the row context, a section path defines a value within the section context,
 * a widget path defines a value within the widget context.
 *
 * Each context is defined similarly to the values. For instance:
 * rowContext: {
 *     widgetPath: 'goal'
 * }
 *
 * Another context is "text", which allows us to look in the i18n files. for instance:
 * {
 *    textPath: "LEWebAgent.VisitorInfoViewController_browser"
 * }
 *
 * Each row/section will define a "display" property, which provides the data needed to display it. Each display object
 * has a number of values (with paths), and a "type" field that indicates how the values should be used and displayed
 * which HTML template/CSS class to use, and where to inject the values.
 * For example:
 *
 * rowContext: {
 *     widgetPath: 'goal'
 * },
 * display: {
 *     type: 'info',
 *     title: {
 *         textPath: 'LEWebAgent.VisitorInfo_goal'
 *     },
 *     value: {
 *         rowPath: 'name'
 *     },
 *     valueTooltip: {
 *         rowPath: 'description'
 *     }
 * }
 *
 * First, a context is defined for the row. Next, the display object contains a 'type' (which is 'info'), and 3 values:
 * title - the text on the left (the "title" of the info row)
 * value - the text on the right (the "value" of the info row)
 * valueTooltip - the tooltip for the value.
 *
 * Most rows will contain some form of title (or other text value) which will be translated, and other values from
 * within the row/section/widget context.
 *
 * Basically, the JSON interpreter/compiler will fill in all the values, and the widget will take the display objects
 * and use the values within to display the row/widget. In row values, apart from the 'path' property we can also have
 * a 'mandatory' field. This means that if the compiler is unable to retrive the value - the row won't be included in
 * the compiled JSON. Also, sections that contain no rows won't be included.
 * Below is a map of mandatory properties per type. A property will only be considered mandatory if it's defined in the
 * configuration. For example, the 'value' of an 'info' row is mandatory. If the JSON specifies a 'value' property -
 * then the compiler will only add the row if the compiled row has a 'value' property. If the JSON doesn't specify a
 * 'value' property - the compiler won't check the compiled row for a 'value' property.
 *
 * Properties that define how to handle a context which is an array. These properties appear within the context object:
 *
 * array (in section or row) - if true means that the context is an array, and that the section/row will be duplicated
 * for each item within the array. Also any reference to the context will refer to each item within the array.
 *
 * Sections can define "innerArray", which means that the section context is an array - but we don't want a duplicate
 * section for each item within the array. Instead, we want to duplicate all the rows within the section for each item.
 *
 * firstInArray/lastInArray (in section or row) - if true means that the context is an array, and that the section/row
 * will only reference the first/last item within the array.
 *
 * Other properties:
 *
 * expandedByDefault - each section can set this property to true or false - to determine if the section is expanded
 * when first displayed. This property can also be set outside the sections - to define a default for any section that
 * didn't define a specific value. The interpreter/compiler will make sure the default value will appear in each
 * section that didn't define a value.
 *
 * remove (in section or row) - if true means that the section/row will be removed by the interpreter/compiler from
 * the final JSON that's given to the widget.
 *
 * If/When customers can customize their visit info widget - the ONLY properties they should add/remove/change are:
 * 'remove' - to remove unwanted rows/sections,
 * 'expandedByDefault' - to change which sections first appear expanded.
 *
 * Anything else - should not be touched outside the team.
 *
 * Also, additional properties may be added to sections and rows.
 *
 * In the case of the visit info - the widget context will be "participants.info.{visitorId}"
 *
 * Flattening the config basically turns the JSON config into a JSON structure that contains only text paths and flat
 * value paths. The meaning of "flat" is a single path without contexts. for example, the following JSON
 * (without things we'll ignore):
 * {
 *     sections: [{
 *         sectionContext: {
 *             widgetPath: "personal",
 *             lastInArray: true
 *         },
 *         display: {
 *             type: "title",
 *             title: {
 *                 textPath: "LEWebAgent.VISIT_INFO_personalInfo"
 *             }
 *         },
 *         rows: [{
 *             rowContext: {
 *                 sectionPath: "contactInfo",
 *                 firstInArray: true
 *             },
 *             display: {
 *                 type: "info",
 *                 title: {
 *                     textPath: "LEWebAgent.VISIT_INFO_email"
 *                 },
 *                 value: {
 *                     rowPath: "email"
 *                 }
 *             }
 *         }]
 *     }]
 * }
 *
 * will be flattened to:
 * [{
 *     name: "LEWebAgent.VISIT_INFO_personalInfo",
 *     rows: [{
 *         name: "LEWebAgent.VISIT_INFO_email",
 *         path: "personal.[-1].contactInfo.[0].email"
 *     }]
 * }]
 */
define(function (require) {

    var lpUtils = require('utils/lpUtils');
    var agentLEManager = require('web-agent-utils/agentLEManager');

    var uniqueInstance;

    var mandatoryProperties = {
        info: ['title', 'value', 'time'],
        auth: ['title', 'value', 'time'],
        unescapedInfo: ['title', 'value'],
        descriptionInfo: ['title', 'value'],
        title: ['title'],
        country: ['title', 'country'],
        link: ['title', 'value'],
        noTitle: ['value']
    };

    function Singleton() {
        Singleton.features = {
            isLE2: !!agentLEManager && !!agentLEManager.hasLiveEngage2Feature && agentLEManager.hasLiveEngage2Feature(),
            isLE2AndNotBlockUserName: !!agentLEManager && !!agentLEManager.hasLiveEngage2Feature && agentLEManager.hasLiveEngage2Feature() && !lpTag.WebAgent.logicController.enableBlockVisitorName()
        };
    }

    Singleton.features = null;

    /////////////////////////////////// PUBLIC METHODS ///////////////////////////////////
    Singleton.prototype.arrayNotations = {
        firstInArray: '[0]',
        lastInArray: '[-1]',
        innerArray: '[i]',
        array: '[]'
    };

    Singleton.prototype.configLocations = require('web-agent-widgets/Config');

    /**
     * Gets the configuration of a widget, according to the name of the widget (in 'configLocations')
     * @param name {string} Name of the widget (in 'configLocations')
     * @returns {*} The JSON config of the widget
     */
    Singleton.prototype.getWidgetConfig = function (name) {
        var config;
        if (name) {
            if (this.configLocations && this.configLocations[name]) {
                config = lpUtils.cloneObject(this.configLocations[name]);
            }
        }
        return config;
    };

    /**
     * Generates the widget data (divided into sections and rows) from the raw data, according to the configuration.
     * @param config {*} JSON config of the widget (what to put where)
     * @param rawData {*} JSON data of the widget (all the data, not organized according to section & row)
     * @returns {*} JSON data for the widget, organized according to the configuration
     */
    Singleton.prototype.generateWidgetData = function (config, rawData) {
        return _compileWidgetConfig({config: config, widgetContext: rawData});
    };

    /**
     * Generates the widget data (divided into sections and rows) from the raw data, according to the name of the widget.
     * @param name {string} Name of the widget (in 'configLocations')
     * @param rawData {*} JSON data of the widget (all the data, not organized according to section & row)
     * @returns {*} JSON data for the widget, organized according to the configuration
     */
    Singleton.prototype.getWidgetData = function(name, rawData) {
        var config = this.getWidgetConfig(name);
        return this.generateWidgetData(config, rawData);
    };

    /**
     * Flattens the JSON config into a text path and value path only JSON, according to the above description.
     * @param name {string} Name of the widget (in 'configLocations')
     * @param [returnPathsOnly] {*} If true - the Flat JSON config will be an array of paths only
     * @returns {*} Flat JSON config
     */
    Singleton.prototype.getFlatWidgetConfig = function(name, returnPathsOnly) {
        var config = this.getWidgetConfig(name);
        return this.flattenWidgetConfig(config, returnPathsOnly);
    };

    /**
     * Flattens the JSON config into a text path and value path only JSON, according to the above description.
     * @param config {*} JSON config of the widget (what to put where)
     * @param [returnPathsOnly] {*} If true - the Flat JSON config will be an array of paths only
     * @returns {*} Flat JSON config
     */
    Singleton.prototype.flattenWidgetConfig = function (config, returnPathsOnly) {
        return _flattenWidgetConfig(config, returnPathsOnly);
    };

    /**
     * Searches for specific data within an array, that can be found at the given path within the raw data
     * @param path {string} Flat path
     * @param rawData {*} JSON data containing the value at the specified path
     * @param searchData {{searchFor: *, searchIn: string|undefined, returnValue: string|undefined}} Contains the search data:
     * searchFor is the value we're searching for in the array (trying to match)
     * searchIn is an optional property name that specifies where to look within each item of the array. If not specified,
     * We try to match the entire item to 'searchFor'
     * returnValue is an optional property name that specifies what to return within the found item of the array. If
     * not specified, we return the entire item from the array
     * @returns {*} The matching item/property within the array
     */
    Singleton.prototype.compileCustomArrayFlatPath = function(path, rawData, searchData) {
        var array = this.compileFlatPath(path, rawData);
        return this.searchWithinArray(array, searchData);
    };

    /**
     * Compiles a flat path to a value
     * @param path {string} Flat path
     * @param rawData {*} JSON data containing the value at the specified path
     * @returns {*} The value found withing the data at the specified path
     */
    Singleton.prototype.compileFlatPath = function(path, rawData) {
        return _getInternalContext(rawData, path);
    };

    /**
     * Searches for specific data within an array
     * @param array {Array} The array to search in
     * @param searchData {{searchFor: *, searchIn: string|undefined, returnValue: string|undefined}} Contains the search data:
     * searchFor is the value we're searching for in the array (trying to match)
     * searchIn is an optional property name that specifies where to look within each item of the array. If not specified,
     * We try to match the entire item to 'searchFor'
     * returnValue is an optional property name that specifies what to return within the found item of the array. If
     * not specified, we return the entire item from the array
     * @returns {*} The matching item/property within the array
     */
    Singleton.prototype.searchWithinArray = function(array, searchData) {
        return _searchWithinArray(array, searchData);
    };

    /////////////////////////////////// PRIVATE METHODS ///////////////////////////////////
    /**
     * Returns a flattened version of the given config
     * @param config {*} JSON config
     * @param [returnPathsOnly] {*} If true - the Flat JSON config will be an array of paths only
     * @returns {Array} Flattened version of the config
     * @private
     */
    function _flattenWidgetConfig(config, returnPathsOnly) {
        var flattened = [];
        if (config && config.sections && config.sections.constructor === Array) {
            for (var sectionIndex = 0; sectionIndex < config.sections.length; sectionIndex++ ) {
                var sectionConfig = config.sections[sectionIndex];
                _flattenSectionConfig({config: sectionConfig, collection: flattened, pathsOnly: returnPathsOnly});
            }
        }
        return flattened;
    }

    /**
     * Creates a flattened version of a given section config, and adds it to a given array of flattened sections.
     * @param data {{config: {}, collection: []}} contains the section config, and the collection (array) of flattened
     * section to add to.
     * @private
     */
    function _flattenSectionConfig(data) {
        var config, collection, pathsOnly;
        if (data) {
            config = data.config;
            collection = data.collection;
            pathsOnly = data.pathsOnly;
        }
        var flattenedSection = {
            rows: []
        };
        if (config && !_shouldRemove(config) && !config.displayOnly) {
            if (config.display) {
                flattenedSection.name = config.display.title && config.display.title.textPath;
            }
            // An empty path will be ignored
            var sectionContextPath = '';
            if (config.sectionContext) {
                sectionContextPath = _getFlatPath({config: config.sectionContext});
            }
            // Unsupported paths will return null
            if (sectionContextPath !== null) {
                var collectionForRows = pathsOnly ? collection : flattenedSection.rows;
                _flattenRowsConfig({config: config.rows, sectionContextPath: sectionContextPath, collection: collectionForRows, pathsOnly: pathsOnly});
            }
            // We only want to add a section that has rows
            if (!pathsOnly && collection && flattenedSection && flattenedSection.rows && flattenedSection.rows.length > 0) {
                collection.push(flattenedSection);
            }
        }
    }

    /**
     * Flattens all the rows within a given array of row configurations, and adds them to the given array of flattened
     * rows.
     * @param data {{config: Array, collection: Array, sectionContextPath: string|undefined}} contains the rows config, and
     * the collection (array) of flattened rows to add to. Contains an optional path for the section context - if one
     * was specified in the original JSON config.
     * @private
     */
    function _flattenRowsConfig(data) {
        var config, sectionContextPath, collection;
        if (data) {
            config = data.config;
            sectionContextPath = data.sectionContextPath;
            collection = data.collection;
        }
        if (config && config.constructor === Array) {
            for (var rowIndex = 0; rowIndex < config.length; rowIndex++ ) {
                // We don't want to add a row that's marked to be removed or for display use only
                if (config && config[rowIndex] && !_shouldRemove(config[rowIndex]) && !config[rowIndex].displayOnly) {
                    _flattenRowConfig({config: config[rowIndex], sectionContextPath: sectionContextPath, collection: collection, pathsOnly: data.pathsOnly});
                }
            }
        }
    }

    /**
     * Flattens a given row configuration, and adds it to the given array of flattened rows.
     * @param data {{config: {}, collection: Array, sectionContextPath: string|undefined}} contains the row config, and
     * the collection (array) of flattened rows to add to. Contains an optional path for the section context - if one
     * was specified in the original JSON config.
     * @private
     */
    function _flattenRowConfig(data) {
        var config, sectionContextPath, collection, pathsOnly;
        if (data) {
            config = data.config;
            sectionContextPath = data.sectionContextPath;
            collection = data.collection;
            pathsOnly = data.pathsOnly;
        }
        // Auth data should not be flattened (and passed in a URL param) for security reasons
        if (config && config.display && config.display.type !== 'auth') {
            // An empty path will be ignored
            var rowContextPath = '';
            if (config.rowContext) {
                rowContextPath = _getFlatPath({config: config.rowContext, sectionContextPath: sectionContextPath});
            }
            // Unsupported paths will return null
            if (rowContextPath !== null) {
                for (var propertyName in config.display) {
                    if (config.display.hasOwnProperty(propertyName) && propertyName !== 'type') {
                        _getFlatNamePath({config: config.display[propertyName], sectionContextPath: sectionContextPath,
                            rowContextPath: rowContextPath, collection: collection, display: config.display,
                            isValue: propertyName === 'value', pathsOnly: pathsOnly});
                    }
                }
            }
        }
    }

    /**
     * Creates a flat name-path-key, based on the data provided. Adds the pair to the array of flattened rows.
     * @param data {{config: {}, collection: Array, sectionContextPath: string|undefined,
     * rowContextPath: string|undefined, display: {}|undefined, isValue: boolean|undefined,
     * pathsOnly: boolean|undefined}} contains the value config, the collection (array) of flattened rows to add to.
     * Contains the following optional fields: path for section context, path for row context, boolean indicating if
     * the config is of the "value" property and a boolean indicating of only the paths should be added to the flat result.
     * @private
     */
    function _getFlatNamePath(data) {
        var config, sectionContextPath, rowContextPath, collection, display, isValue, pathsOnly;
        if (data) {
            config = data.config;
            sectionContextPath = data.sectionContextPath;
            rowContextPath = data.rowContextPath;
            collection = data.collection;
            display = data.display;
            isValue = data.isValue;
            pathsOnly = data.pathsOnly;
        }
        var flattenedData = {};
        if (config) {
            flattenedData.path = _getFlatPath({config: config, sectionContextPath: sectionContextPath, rowContextPath: rowContextPath});
            flattenedData.key = config.key;
            if (config.isIdentifier === true || config.isIdentifier === false) {
                flattenedData.isIdentifier = config.isIdentifier;
            }
            // If we only want the path - we add the path. If not - we get the name as well.
            if (pathsOnly) {
                if (flattenedData.path && collection) {
                    collection.push(flattenedData.path);
                }
            } else {
                if (config.flatName) {
                    // If a flat name is specified - we use it
                    flattenedData.name = config.flatName;
                } else if (display && isValue) {
                    // If not, and this is the "value" - we use the title
                    flattenedData.name = display.title && display.title.textPath;
                }
            }
        }
        // We only want to add a row that has a name and value
        if (!pathsOnly && collection && flattenedData && flattenedData.name && flattenedData.path) {
            collection.push(flattenedData);
        }
    }

    /**
     * Gets the flat path of a value, based on the path type and the given context paths
     * @param data {{config: {widgetPath: string|undefined, sectionPath: string|undefined, rowPath: string|undefined},
     * sectionContextPath: string|undefined, rowContextPath: string|undefined}} contains the config of the value path
     * to flatten (containing a path property of some sort) and optional paths for the section context & row context.
     * @returns {string} Flat path of the value. Paths containing array:true or innerArray: true return null, and are
     * ignored.
     * @private
     */
    function _getFlatPath(data) {
        var config, sectionContextPath, rowContextPath;
        if (data) {
            config = data.config;
            sectionContextPath = data.sectionContextPath;
            // If a row path is specified, we add the path of the row context. However, if there is no row context,
            // we add the path of the section context instead.
            rowContextPath = data.rowContextPath || sectionContextPath;
        }
        var contextPath = '';
        if (config) {
            if (config.widgetPath) {
                contextPath = config.widgetPath;
            } else if (config.sectionPath) {
                if (sectionContextPath && sectionContextPath.length > 0) {
                    contextPath = sectionContextPath + '.';
                }
                contextPath += config.sectionPath;
            } else if (config.rowPath) {
                if (rowContextPath && rowContextPath.length > 0) {
                    contextPath = rowContextPath + '.';
                }
                contextPath += config.rowPath;
            }
            var arrayNotations = Singleton.prototype.arrayNotations;
            if (config.firstInArray) {
                contextPath += '.' + arrayNotations.firstInArray;
            } else if (config.lastInArray) {
                contextPath += '.' + arrayNotations.lastInArray;
            } else if (config.array || config.innerArray) {
                // At the moment we don't support flattening arrays or inner arrays,
                // so we return null (and ignore the path)
                contextPath = null;
            }
        }
        return contextPath;
    }

    /**
     * Returns the compiled data from a given config and widget context (raw data).
     * @param data {{config: {}, widgetContext: {}}} contains the config for the widget, and the widget context (raw
     * data).
     * @returns {{search: boolean|undefined, sections: Array}} Compiled data, divided into sections and rows.
     * @private
     */
    function _compileWidgetConfig(data) {
        var config, widgetContext;
        if (data) {
            config = data.config;
            widgetContext = data.widgetContext;
        }
        var compiled = {
            sections: []
        };
        if (config) {
            compiled.search = config.search;
            var expandedByDefault = false;
            if (typeof config.expandedByDefault !== 'undefined') {
                expandedByDefault = config.expandedByDefault;
            }
            if (config.sections && config.sections.constructor === Array) {
                for (var sectionIndex = 0; sectionIndex < config.sections.length; sectionIndex++ ) {
                    var sectionConfig = config.sections[sectionIndex];
                    if (sectionConfig && !_shouldRemove(sectionConfig) && !sectionConfig.flatOnly) {
                        _getContextAndHandleArrayAttributes({config: config.sections[sectionIndex], nameForContext: 'sectionContext', callback: _compileSectionConfig, collection: compiled.sections, expandedByDefault: expandedByDefault, widgetContext: widgetContext});
                    }
                }
            }
        }
        return compiled;
    }

    /**
     * Here we extract a context element (according to a given name), and check to see if it's an array. If it's an
     * array, and specifies how to handle the array (array, innerArray, firstInArray, lastInArray) - we call the
     * callback with the appropriate array handling. If it's not an array - we call the callback with the context as is.
     * @param data {{config: {}, callback: function, nameForContext: string}} contains the config (which will be passed
     * on to the callback), the callback function, and the name for the context element we want to extract. There may be
     * additional optional parameters which will be passed on to the callback function.
     * @private
     */
    function _getContextAndHandleArrayAttributes(data) {
        var config, nameForContext, callback, context;
        if (data) {
            config = data.config;
            callback = data.callback;
            nameForContext = data.nameForContext;
        }
        if (data && config && callback && nameForContext) {
            var newData = {};
            // we want a by-reference copy of the data - to pass on to the callback
            for (var propertyName in data) {
                if (data.hasOwnProperty(propertyName) && propertyName !== 'context' && propertyName !== 'callback' && propertyName !== 'nameForContext') {
                    newData[propertyName] = data[propertyName];
                }
            }
            // The context data, on the other hand, is just used to compile a single value. We don't need it to be by-reference
            var contextData = lpUtils.cloneObject(newData);
            if (config[nameForContext]) {
                contextData.config = config[nameForContext];
                context = _compileValue(contextData);
            }
            if (context && context.constructor === Array) {
                if (config[nameForContext].array) {
                    for (var arrayIndex = 0; arrayIndex < context.length; arrayIndex++ ) {
                        newData[nameForContext] = context[arrayIndex];
                        callback(newData);
                    }
                } else if (config[nameForContext].firstInArray && context.length > 0) {
                    newData[nameForContext] = context[0];
                    callback(newData);
                } else if (config[nameForContext].lastInArray && context.length > 0) {
                    newData[nameForContext] = context[context.length - 1];
                    callback(newData);
                } else {
                    newData[nameForContext] = context;
                    callback(newData);
                }
            } else {
                if (context) {
                    newData[nameForContext] = context;
                }
                callback(newData);
            }
        }
    }

    /**
     * Compiles a section according to a given config, and adds it to a given collection (array) of compiled sections.
     * @param data {{config: {}, widgetContext: {}, sectionContext: {}|undefined, collection: Array,
     * expandedByDefault: boolean}} contains the config for the section, the widget context (raw data), the
     * section context (if specified), the collection (array) of compiled sections and the default value for
     * "expandedByDefault".
     * @private
     */
    function _compileSectionConfig(data) {
        var sectionConfig, expandedByDefault, widgetContext, sectionContext, collection;
        if (data) {
            sectionConfig = data.config;
            widgetContext = data.widgetContext;
            sectionContext = data.sectionContext;
            collection = data.collection;
            expandedByDefault = data.expandedByDefault;
        }
        var compiledSection = {
            rows: []
        };
        // We don't want to add a section that's marked to be removed
        if (sectionConfig && !_shouldRemove(sectionConfig)) {
            for (var propertyName in sectionConfig) {
                if (sectionConfig.hasOwnProperty(propertyName)) {
                    if (propertyName !== 'sectionContext' && propertyName !== 'remove' &&
                        propertyName !== 'expandedByDefault' && propertyName !== 'display' && propertyName !== 'rows' &&
                        propertyName !== 'featureEnabled' && propertyName !== 'featureDisabled') {
                            compiledSection[propertyName] = sectionConfig[propertyName];
                    }
                }
            }
            compiledSection.expandedByDefault = expandedByDefault;
            if (typeof sectionConfig.expandedByDefault !== 'undefined') {
                compiledSection.expandedByDefault = sectionConfig.expandedByDefault;
            }
            if (sectionConfig.display) {
                compiledSection.display = _compileDisplayConfig({config: sectionConfig.display, widgetContext: widgetContext, sectionContext: sectionContext});
                if (compiledSection.display.extendId) {
                    if (compiledSection.id) {
                        compiledSection.id += '.' + compiledSection.display.extendId;
                    }
                    delete compiledSection.display.extendId;
                }
            }
            // If the section context is an inner array - we need to make sure the rows are all put in the same section.
            if (sectionConfig.sectionContext && sectionConfig.sectionContext.innerArray) {
                if (sectionContext && sectionContext.length) {
                    for (var arrayIndex = 0; arrayIndex < sectionContext.length; arrayIndex++ ) {
                        _compileRowsConfig({config: sectionConfig.rows, widgetContext: widgetContext, sectionContext: sectionContext[arrayIndex], collection: compiledSection.rows});
                    }
                }
            } else {
                _compileRowsConfig({config: sectionConfig.rows, widgetContext: widgetContext, sectionContext: sectionContext, collection: compiledSection.rows});
            }
        }
        // We only add the section if it contains rows
        if (collection && compiledSection && compiledSection.rows && compiledSection.rows.length > 0) {
            collection.push(compiledSection);
        }
    }

    /**
     * Compiles all the rows within a given array of row configurations and adds them to the given array of compiled
     * rows.
     * @param data {{config: Array, collection: Array, widgetContext: {}, sectionContext: {}|undefined}} contains the
     * config array for all the rows, the collection array of compiled rows, the widgetContext (raw data) and the
     * optional sectionContext.
     * @private
     */
    function _compileRowsConfig(data) {
        var rowsConfig, widgetContext, sectionContext, collection;
        if (data) {
            rowsConfig = data.config;
            widgetContext = data.widgetContext;
            sectionContext = data.sectionContext;
            collection = data.collection;
        }
        if (rowsConfig && rowsConfig.length) {
            for (var rowIndex = 0; rowIndex < rowsConfig.length; rowIndex++ ) {
                // We don't want to add a row that's marked to be removed or for flat use only
                if (rowsConfig[rowIndex] && !_shouldRemove(rowsConfig[rowIndex]) && !rowsConfig[rowIndex].flatOnly) {
                    _getContextAndHandleArrayAttributes({config:rowsConfig[rowIndex], nameForContext: 'rowContext', callback: _compileRowConfig, collection: collection, widgetContext: widgetContext, sectionContext: sectionContext});
                }
            }
        }
    }

    /**
     * Compiles a given row configuration and adds it to the given array of compiled rows.
     * @param data {{collection: [], config: {}, widgetContext: {}, sectionContext: {}|undefined, rowContext:
     * {duplicateTimes: string|number|undefined}|undefined}} contains the configuration of the row, the collection
     * (array) of the compiled rows, the widget context (raw data) and optionally the section context and row context.
     * @private
     */
    function _compileRowConfig(data) {
        var rowConfig, widgetContext, sectionContext, rowContext, collection;
        if (data) {
            collection = data.collection;
            rowConfig = data.config;
            widgetContext = data.widgetContext;
            sectionContext = data.sectionContext;
            rowContext = data.rowContext;
        }
        if (collection) {
            var compiledRow = {};
            if (rowConfig) {
                for (var propertyName in rowConfig) {
                    if (rowConfig.hasOwnProperty(propertyName)) {
                        if (propertyName !== 'rowContext' && propertyName !== 'remove' &&
                            propertyName !== 'display' && propertyName !== 'duplicateTimes' &&
                            propertyName !== 'featureEnabled' && propertyName !== 'featureDisabled') {
                                compiledRow[propertyName] = rowConfig[propertyName];
                        }
                    }
                }
                if (rowConfig.display) {
                    var innerArray = !!rowConfig.rowContext && !!rowConfig.rowContext.innerArray;
                    compiledRow.display = _compileDisplayConfig({config: rowConfig.display, widgetContext: widgetContext, sectionContext: sectionContext, rowContext: rowContext, rowInnerArray: innerArray});
                }
                // If the config specifies how many times to duplicate the row - we duplicate it
                if (rowConfig.duplicateTimes) {
                    var duplicate = _compileValue({config: rowConfig.duplicateTimes, widgetContext: widgetContext, sectionContext: sectionContext, rowContext: rowContext});
                    if (duplicate && typeof duplicate === 'string') {
                        duplicate = parseInt(duplicate, 10);
                    }
                    if (duplicate && typeof duplicate === 'number' && duplicate > 0) {
                        for (var index = 0; index < duplicate; index++ ) {
                            _addCompiledRow(compiledRow, rowConfig, collection);
                        }
                    } else {
                        _addCompiledRow(compiledRow, rowConfig, collection);
                    }
                } else {
                    _addCompiledRow(compiledRow, rowConfig, collection);
                }
            }
        }
    }

    /**
     * Adds a given compiled row to the given array of compiled rows. Makes sure the row is 'legal' before adding it,
     * meaning that it contains all values marked as mandatory (both explicitly and implicitly), and that it doesn't
     * only contain empty values.
     * @param compiledRow {*} The compiled row
     * @param rowConfig {*} The row config
     * @param collection {Array} The array of compiled rows to add to.
     * @private
     */
    function _addCompiledRow(compiledRow, rowConfig, collection) {
        if (collection && compiledRow && compiledRow.display && rowConfig && rowConfig.display) {
            var type = rowConfig.display.type;
            var shouldAdd = true;
            var allEmpty = true;
            for (var propertyName in rowConfig.display) {
                if (rowConfig.display.hasOwnProperty(propertyName) && propertyName !== 'type') {
                    var isMandatory = false;
                    // If the property has a "mandatory" field, we use it
                    if (typeof rowConfig.display[propertyName].mandatory !== 'undefined') {
                        isMandatory = rowConfig.display[propertyName].mandatory;
                    // If not, we check the defaults based on the row type
                    } else {
                        isMandatory = mandatoryProperties[type] && mandatoryProperties[type].indexOf(propertyName) >= 0;
                    }
                    var value = compiledRow.display[propertyName];
                    if (isMandatory) {
                        // if the compiled row doesn't have the mandatory value, we shouldn't add it
                        if (_isEmptyValue(value)) {
                            shouldAdd = false;
                            break;
                        }
                    }
                    // If all the properties are empty (not counting 'type' and tooltips or prefixes) we don't want to add the row
                    if (propertyName.toLowerCase().indexOf('tooltip') < 0 && propertyName.toLowerCase().indexOf('prefix') < 0 && !_isEmptyValue(value, rowConfig.display[propertyName])) {
                        allEmpty = false;
                    }
                }
            }
            if (shouldAdd && !allEmpty) {
                collection.push(compiledRow);
            }
        }
    }

    /**
     * Checks if a value is empty, or a default value
     * @param value The value to check
     * @param [config] The optional configuration possibly containing a default value
     * @returns {boolean|*}
     * @private
     */
    function _isEmptyValue(value, config) {
        return (_isEmptyPattern(value) || (value.constructor === Array && _isArrayEmptyValues(value))) ||
            (config && typeof config.defaultValue !== 'undefined' && value === config.defaultValue);
    }

    function _isArrayEmptyValues(arrayValue) {
        if(arrayValue.length === 0) {
            return true;
        }
        for (var i=0; i<arrayValue.length; i++) {
            if(!_isEmptyPattern(arrayValue[i])) {
                return false;
            }
        }
        return true;
    }

    function _isEmptyPattern(value) {
        return (typeof value === 'undefined' || value === null || value === '' || value === 'N/A' || value === '--');
    }

    /**
     * Returns a compiled display value, based on a given display configuration and context objects.
     * @param data {{config: {}, widgetContext: {}, sectionContext: {}|undefined, rowContext: {}|undefined, rowInnerArray: boolean|undefined}} contains
     * the config display element, the widget context (raw data) and optional section context & row context.
     * @returns {{}} The compiled display element
     * @private
     */
    function _compileDisplayConfig(data) {
        var displayConfig, widgetContext, sectionContext, rowContext;
        if (data) {
            displayConfig = data.config;
            widgetContext = data.widgetContext;
            sectionContext = data.sectionContext;
            rowContext = data.rowContext;
        }
        var compiledDisplay = {};
        if (displayConfig) {
            for (var property in displayConfig) {
                if (displayConfig.hasOwnProperty(property)) {
                    if (property === 'type') {
                        compiledDisplay.type = displayConfig[property];
                    } else {
                        compiledDisplay[property] = _compileValue({config: displayConfig[property], widgetContext: widgetContext, sectionContext: sectionContext, rowContext: rowContext, rowInnerArray: data.rowInnerArray});
                    }
                }
            }
        }
        return compiledDisplay;
    }

    /**
     * Compiles a single value from a display element, according to the given config and context objects.
     * @param data {{config: {}, widgetContext: {}, sectionContext: {}|undefined, rowContext: {}|undefined, rowInnerArray: boolean|undefined}} contains
     * the value config, the widget context and optional section context & row context.
     * @returns {*} The compiled value.
     * @private
     */
    function _compileValue(data) {
        var config, widgetContext, sectionContext, rowContext, rowInnerArray;
        if (data) {
            config = data.config;
            widgetContext = data.widgetContext;
            sectionContext = data.sectionContext || data.widgetContext;
            rowContext = data.rowContext || data.sectionContext || data.widgetContext;
            rowInnerArray = data.rowInnerArray;
        }
        var compiled = null;
        if (config) {
            if (config.textPath) {
                if (agentLEManager && agentLEManager.translateLE) {
                    compiled = agentLEManager.translateLE(config.textPath);
                }
            } else if (config.rowPath) {
                if (rowInnerArray && rowContext && rowContext.constructor === Array ) {
                    compiled = [];
                    for (var i = 0; i < rowContext.length; i++) {
                        var innerCompiled = _getInternalContext(rowContext[i], config.rowPath);
                        if (typeof innerCompiled !== 'undefined' && innerCompiled !== null) {
                            compiled.push(innerCompiled);
                        }
                    }
                } else {
                    compiled = _getInternalContext(rowContext, config.rowPath);
                }
            } else if (config.sectionPath) {
                compiled = _getInternalContext(sectionContext, config.sectionPath);
            } else if (config.widgetPath) {
                compiled = _getInternalContext(widgetContext, config.widgetPath);
            }
        }
        // If we get null or undefined - we return a default value (if one is provided)
        if ((typeof compiled === 'undefined' || compiled === null) && typeof config.defaultValue !== 'undefined' ) {
            compiled = config.defaultValue;
        }
        return compiled;
    }

    /**
     * Gets a value/object from within a given context, according to a specified path. Optionally uses pre-determined
     * array notations for retrieving values within arrays.
     * @param context JSON object which will be searched for our return value
     * @param path {string} path specifying the location of the return value within the context object
     * @param [useArrayNotations] {boolean}. If true - we check for array notations when going through the path.
     * @returns {*} The value/object found at the specified path within the context object
     * @private
     */
    function _getInternalContext(context, path) {
        var innerContext = null;
        if (context && path) {
            var pathSteps = path.split(".");
            // find out if any of the steps include an array notation- then parse them
            pathSteps = parseArrayNotations(pathSteps);
            innerContext = context;
            if (pathSteps && pathSteps.length) {
                for (var index = 0; index < pathSteps.length; index++ ) {
                    if (pathSteps[index] && innerContext) {
                        if (_isArrayNotation(pathSteps[index], innerContext)) {
                            innerContext = _getArrayNotationContext(pathSteps[index], innerContext);
                        }
                        else if (innerContext.hasOwnProperty(pathSteps[index])) {
                            innerContext = innerContext[pathSteps[index]];
                        } else {
                            innerContext = null;
                        }
                    } else {
                        innerContext = null;
                    }
                }
            }
        }
        return innerContext;
    }

    function parseArrayNotations(pathArr) {
        var regex = /\[{1}(0|\-1|i)?\]{1}$/;
        var res, resIndex;
        var resultArr = [];
        for (var path in pathArr) {
            res = pathArr[path].match(regex);
            resIndex = res && res.index;
            if (res && res[0] && resIndex) {
                resultArr.push(pathArr[path].substring(0, resIndex));
                resultArr.push(pathArr[path].substring(resIndex));
            }
            else {
                resultArr.push(pathArr[path]);
            }
        }
        return resultArr;
    }

    /**
     * Searches for specific data within an array
     * @param array {Array} The array to search in
     * @param searchData {{searchFor: *, searchIn: string|undefined, returnValue: string|undefined}} Contains the search data:
     * searchFor is the value we're searching for in the array (trying to match)
     * searchIn is an optional property name that specifies where to look within each item of the array. If not specified,
     * We try to match the entire item to 'searchFor'
     * returnValue is an optional property name that specifies what to return within the found item of the array. If
     * not specified, we return the entire item from the array
     * @returns {*} The matching item/property within the array
     * @private
     */
    function _searchWithinArray(array, searchData) {
        var found = null;
        if (array && array.length > 0 && searchData.searchFor) {
            for (var index = 0; index < array.length; index++ ) {
                var lookIn = array[index];
                if (lookIn) {
                    // If searchIn is specified - we only try to match that property
                    if (searchData.searchIn) {
                        lookIn = lookIn[searchData.searchIn];
                    }
                }
                if (lookIn && lookIn === searchData.searchFor) {
                    found = array[index];
                    break;
                }
            }
            // If returnValue is specified - we only return that property. If not, we return the entire item
            if (found && searchData.returnValue) {
                found = found[searchData.returnValue];
            }
        }
        return found;
    }

    /**
     *
     * @param pathStep {string} The step to take within the context
     * @param context {*} The context
     * @returns {boolean} True if the step is an array notation and the context is an array
     * @private
     */
    function _isArrayNotation(pathStep, context) {
        var arrayNotations = Singleton.prototype.arrayNotations;
        return arrayNotations.hasOwnProperty(pathStep) >= 0 && context.constructor === Array && context.length > 0;
    }

    /**
     * Returns an element from an array according to the array notations
     * @param pathStep {string} The step to take within the context
     * @param array {Array} The array to search within
     * @returns {*} The appropriate value within the array
     * @private
     */
    function _getArrayNotationContext(pathStep, array) {
        var arrayNotations = Singleton.prototype.arrayNotations;
        switch (pathStep) {
            case arrayNotations.firstInArray:
                return array[0];
            case arrayNotations.lastInArray:
                return array[array.length - 1];
            default:
                // At the moment we don't support array/inner array for flat path compilation
                return null;
        }
    }

    /**
     * Returns true if the configuration (of the section/row) indicates that it should be removed.
     * @param config Can contain the following optional properties:
     * remove - if true, should be removed.
     * featureEnabled - if the given feature name is enabled - the section/row should be included.
     * featureDisabled - if the given feature name is not enabled - the section/row should be included.
     * @returns {*} True if the configuration shows that the section/row should be removed.
     * @private
     */
    function _shouldRemove(config) {
        if (config.remove) {
            return true;
        }
        if (config.featureEnabled && Singleton.features.hasOwnProperty(config.featureEnabled)) {
            return !Singleton.features[config.featureEnabled];
        }
        if (config.featureDisabled && Singleton.features.hasOwnProperty(config.featureDisabled)) {
            return Singleton.features[config.featureDisabled];
        }
    }

    return {
        getInstance: function() {
            if (!uniqueInstance) {
                uniqueInstance = new Singleton();
            }
            return uniqueInstance;
        },
        features: function() {
            return Singleton.features;
        }
    };

});
